import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import { BackgroundImageKey } from '../constants/imageKeys';
import { ACHIEVEMENT_META } from '../constants/achievementConfig';
import { AchievementId } from '../types/Achievement';
import { loadAchievements } from '../utils/achievementManager';

const ITEMS_PER_COL = 8;
const COL_WIDTH = Math.floor(GAME_WIDTH / 3); // 320px

const LAYOUT = {
  titleY: 35,
  counterY: 68,
  listStartY: 110,
  rowHeight: 48,
  iconOffsetX: 8,
  nameOffsetX: 28,
  descOffsetX: 28,
  nameOffsetY: -10,
  descOffsetY: 12,
  backButtonY: 480,
} as const;

const ALL_IDS = Object.values(AchievementId);

/**
 * 実績一覧画面
 *
 * 解除済み実績は名前・説明を表示。
 * 未解除実績は名前を「???」でマスクし、説明は非表示（ネタバレ防止）。
 */
export class AchievementScene extends BaseScene {
  constructor() {
    super(SceneKey.ACHIEVEMENT);
  }

  create(): void {
    if (this.textures.exists(BackgroundImageKey.TITLE)) {
      this.add
        .image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.TITLE)
        .setOrigin(0.5)
        .setAlpha(0.3);
    }

    const { unlocked } = loadAchievements();
    const unlockedSet = new Set(unlocked);

    this.createTitle();
    this.createCounter(unlockedSet.size);
    this.createList(unlockedSet);
    this.createBackButton();
  }

  private createTitle(): void {
    this.add
      .text(GAME_WIDTH / 2, LAYOUT.titleY, '実績', {
        fontSize: '28px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
  }

  private createCounter(unlockedCount: number): void {
    this.add
      .text(GAME_WIDTH / 2, LAYOUT.counterY, `${unlockedCount} / ${ALL_IDS.length} 解除`, {
        fontSize: '18px',
        color: '#cccccc',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);
  }

  private createList(unlockedSet: Set<string>): void {
    ALL_IDS.forEach((id, index) => {
      const colIndex = Math.floor(index / ITEMS_PER_COL);
      const rowIndex = index % ITEMS_PER_COL;
      const colX = colIndex * COL_WIDTH;
      const rowY = LAYOUT.listStartY + rowIndex * LAYOUT.rowHeight;
      const isUnlocked = unlockedSet.has(id);
      const meta = ACHIEVEMENT_META[id];

      const iconX = colX + LAYOUT.iconOffsetX;
      const nameX = colX + LAYOUT.nameOffsetX;
      const nameY = rowY + LAYOUT.nameOffsetY;
      const descX = colX + LAYOUT.descOffsetX;
      const descY = rowY + LAYOUT.descOffsetY;

      this.add
        .text(iconX, nameY, isUnlocked ? '★' : '☆', {
          fontSize: '14px',
          color: isUnlocked ? '#ffd700' : '#555555',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
        })
        .setOrigin(0, 0.5);

      this.add
        .text(nameX, nameY, isUnlocked ? meta.name : '???', {
          fontSize: '16px',
          color: isUnlocked ? '#ffffff' : '#666666',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          fontStyle: isUnlocked ? 'bold' : 'normal',
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setOrigin(0, 0.5);

      if (isUnlocked) {
        this.add
          .text(descX, descY, meta.description, {
            fontSize: '11px',
            color: '#aaaaaa',
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
            stroke: '#000000',
            strokeThickness: 2,
          })
          .setOrigin(0, 0.5);
      }
    });
  }

  private createBackButton(): void {
    this.add
      .text(GAME_WIDTH / 2, LAYOUT.backButtonY, '← 戻る', {
        fontSize: '22px',
        color: '#aaaaff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.transitionTo(SceneKey.TITLE));
  }
}
